import React, { useState, useEffect } from "react";
import UserModel from "./types";

interface CommunicationPreferencesProps {
  profileData: UserModel;
  isEditing: boolean;
  onUpdate: (data: Partial<UserModel>) => Promise<void>;
  isSaving: boolean;
}

const CommunicationPreferences: React.FC<CommunicationPreferencesProps> = ({
  profileData,
  isEditing,
  onUpdate,
  isSaving,
}) => {
  const [preferences, setPreferences] = useState({
    promoOffers: profileData.promoOffers,
    eventUpdates: profileData.eventUpdates,
    textMessages: profileData.textMessages,
  });

  // Reset preferences when profileData changes or when exiting edit mode
  useEffect(() => {
    setPreferences({
      promoOffers: profileData.promoOffers,
      eventUpdates: profileData.eventUpdates,
      textMessages: profileData.textMessages,
    });
  }, [profileData, isEditing]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setPreferences((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onUpdate(preferences);
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h3 className="text-xl font-semibold mb-4">Communication Preferences</h3>

      <form onSubmit={handleSubmit}>
        <div className="space-y-4 mb-6">
          {/* Promotional Offers */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="promoOffers"
                name="promoOffers"
                type="checkbox"
                checked={preferences.promoOffers}
                onChange={handleChange}
                disabled={!isEditing || isSaving}
                className="w-4 h-4 text-cannabis-green-500 rounded border-gray-600 bg-gray-700 focus:ring-cannabis-green-400"
              />
            </div>
            <div className="ml-3">
              <label
                htmlFor="promoOffers"
                className={`${isEditing ? "text-white" : "text-gray-300"}`}
              >
                Promotional Offers
              </label>
              <p className="text-sm text-gray-400">
                Receive emails about special deals and promotions.
              </p>
            </div>
          </div>

          {/* Event Updates */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="eventUpdates"
                name="eventUpdates"
                type="checkbox"
                checked={preferences.eventUpdates}
                onChange={handleChange}
                disabled={!isEditing || isSaving}
                className="w-4 h-4 text-cannabis-green-500 rounded border-gray-600 bg-gray-700 focus:ring-cannabis-green-400"
              />
            </div>
            <div className="ml-3">
              <label
                htmlFor="eventUpdates"
                className={`${isEditing ? "text-white" : "text-gray-300"}`}
              >
                Event Updates
              </label>
              <p className="text-sm text-gray-400">
                Get notified about upcoming events and community gatherings.
              </p>
            </div>
          </div>

          {/* Text Messages */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="textMessages"
                name="textMessages"
                type="checkbox"
                checked={preferences.textMessages}
                onChange={handleChange}
                disabled={!isEditing || isSaving}
                className="w-4 h-4 text-cannabis-green-500 rounded border-gray-600 bg-gray-700 focus:ring-cannabis-green-400"
              />
            </div>
            <div className="ml-3">
              <label
                htmlFor="textMessages"
                className={`${isEditing ? "text-white" : "text-gray-300"}`}
              >
                Text Messages
              </label>
              <p className="text-sm text-gray-400">
                Receive SMS notifications for important updates and offers.
              </p>
            </div>
          </div>
        </div>

        {isEditing && (
          <div className="flex justify-end">
            <button
              type="submit"
              className="px-4 py-2 bg-cannabis-green-500 hover:bg-cannabis-green-600 rounded-lg transition flex items-center"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving...
                </>
              ) : (
                "Save Preferences"
              )}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default CommunicationPreferences;
